import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';

import { cn } from '~utils/cn';

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-primary dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        xs: "h-6 gap-1 rounded-md text-xs [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md',
        default: 'h-10',
        lg: 'h-12 rounded-md',
      },
      /**
       * When `true`, the button is rendered as an equal-width square sized to
       * match the chosen `size` (see compoundVariants). Combine with `size`
       * instead of using dedicated `icon-*` sizes, e.g. `<Button icon size="sm" />`.
       */
      icon: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Text buttons: horizontal padding scales with size (tighter when an icon child is present).
      { icon: false, size: 'xs', className: 'px-2 has-[>svg]:px-1.5' },
      { icon: false, size: 'sm', className: 'px-3 has-[>svg]:px-2.5' },
      { icon: false, size: 'default', className: 'px-6 has-[>svg]:px-4' },
      { icon: false, size: 'lg', className: 'px-8 has-[>svg]:px-6' },
      // Icon-only buttons: square, width matches height per size.
      { icon: true, size: 'xs', className: 'size-6' },
      { icon: true, size: 'sm', className: 'size-8' },
      { icon: true, size: 'default', className: 'size-10' },
      { icon: true, size: 'lg', className: 'size-12' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      icon: false,
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  icon = false,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-icon={icon}
      className={cn(buttonVariants({ variant, size, icon, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
